import React, {
  useRef, useEffect, useState
} from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { FormLayoutContent, FormLayout } from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import { RootState } from 'data/root.reducers';
import * as securityPolicyActions from 'data/securityPolicy/securityPolicy.actions';
import * as securityPolicySelectors from 'data/securityPolicy/securityPolicy.selectors';
import * as securityPolicyTypes from 'data/securityPolicy/securityPolicy.types';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import { Ref } from '../../components/SecurityPolicyForm/SecurityPolicyForm';
import {
  adaptTemplateVisibilityMode,
  adaptSecurityPolicyData,
  adaptSecurityPolicyTemplate,
} from '../../utils/securityPolicyTemplate.adapter';
import { securityPolicyTemplate } from '../../templates';
import SecurityPolicyForm from '../../components/SecurityPolicyForm';
import ErrorsDialog from '../../components/ErrorsDialog';

type RouterParams = {
  history: string
}

const mapState = (state: RootState) => ({
  deliveryFunctionId: state.global.selectedDF,
  securityPolicyData: securityPolicySelectors.getSecurityPolicy(state),
  securityPolicyErrors: securityPolicySelectors.getSecurityPolicyErrors(state),
  uiState: securityPolicySelectors.getUiState(state),
  formTemplate: adaptTemplateVisibilityMode(
    securityPolicyTemplate,
    'view',
  ),
});

const mapDispatch = {
  getSecurityPolicy: securityPolicyActions.getSecurityPolicy,
  goToSecurityPolicyEdit: securityPolicyActions.goToSecurityPolicyEdit,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter;

export const SecurityPolicyView = ({
  history,
  securityPolicyData,
  deliveryFunctionId,
  formTemplate,
  securityPolicyErrors,
  uiState,
  getSecurityPolicy,
  goToSecurityPolicyEdit,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const [template, setTemplate] = useState(formTemplate);
  const [formData, setFormData] = useState<Partial<{
    passwordSettings: securityPolicyTypes.Password,
    accountLockoutSettings: securityPolicyTypes.AccountLockout,
    ipAddressBlockingSettings: securityPolicyTypes.IpAddressBlocking,
  }>>({});
  const formRef = useRef<Ref>(null);

  useEffect(() => {
    if (deliveryFunctionId == null) {
      return;
    }
    getSecurityPolicy(deliveryFunctionId)
  }, [
    deliveryFunctionId,
    getSecurityPolicy,
  ]);

  useEffect(() => {
    const clonedSecurityPolicy: securityPolicyTypes.SecurityPolicyAdaptedData = cloneDeep(adaptSecurityPolicyData(securityPolicyData));
    const newFormData: Partial<{
      passwordSettings: securityPolicyTypes.Password,
      accountLockoutSettings: securityPolicyTypes.AccountLockout,
      ipAddressBlockingSettings: securityPolicyTypes.IpAddressBlocking,
    }> = {};
    newFormData.passwordSettings = {
      id: clonedSecurityPolicy.id,
      minimumPasswordLength: clonedSecurityPolicy.minimumPasswordLength,
      uniquePasswordHistoryCount: clonedSecurityPolicy.uniquePasswordHistoryCount,
      passwordExpirationDays: clonedSecurityPolicy.passwordExpirationDays,
      requireUppercase: clonedSecurityPolicy.requireUppercase,
      requireLowercase: clonedSecurityPolicy.requireLowercase,
      requireNumber: clonedSecurityPolicy.requireNumber,
      requireSymbol: clonedSecurityPolicy.requireSymbol,
      allowNumericSequences: clonedSecurityPolicy.allowNumericSequences,
      maxConsecutiveCharacters: clonedSecurityPolicy.maxConsecutiveCharacters,
      allowPasswordUsernameMatch: clonedSecurityPolicy.allowPasswordUsernameMatch,
      allowCurrentPasswordReuse: clonedSecurityPolicy.allowCurrentPasswordReuse,
    }

    newFormData.accountLockoutSettings = {
      id: clonedSecurityPolicy.id,
      enableAccountLockout: clonedSecurityPolicy.enableAccountLockout,
      maxAccountLoginAttempts: clonedSecurityPolicy.maxAccountLoginAttempts,
      accountLoginAttemptsIntervalMinutes: clonedSecurityPolicy.accountLoginAttemptsIntervalMinutes,
      accountLockDurationMinutes: clonedSecurityPolicy.accountLockDurationMinutes,
      applyToSystemAdminAccount: clonedSecurityPolicy.applyToSystemAdminAccount,
    }

    newFormData.ipAddressBlockingSettings = {
      id: clonedSecurityPolicy.id,
      enableIpAddressBlocking: clonedSecurityPolicy.enableIpAddressBlocking,
      maxIpAddressLoginAttempts: clonedSecurityPolicy.maxIpAddressLoginAttempts,
      ipAddressLoginAttemptsIntervalMinutes: clonedSecurityPolicy.ipAddressLoginAttemptsIntervalMinutes,
      ipAddressBlockDurationMinutes: clonedSecurityPolicy.ipAddressBlockDurationMinutes,
    }

    let clonedTemplate = adaptSecurityPolicyTemplate(formTemplate, 'enableAccountLockout', clonedSecurityPolicy.enableAccountLockout);
    clonedTemplate = adaptSecurityPolicyTemplate(clonedTemplate, 'enableIpAddressBlocking', clonedSecurityPolicy.enableIpAddressBlocking);

    setFormData(newFormData);
    setTemplate(clonedTemplate);
  }, [
    securityPolicyData,
    formTemplate
  ]);

  const handleClickEdit = () => {
    goToSecurityPolicyEdit(history);
  };

  const renderForm = () => {
    if (!template || uiState.isLoading) {
      return null;
    }

    return (
      <div>
        <SecurityPolicyForm
          ref={formRef}
          data={formData}
          template={template}
        />
      </div>
    );
  };

  const renderFormPanel = () => {
    return (
      <FormPanel title={t('securityPolicy.view.title')}>
        <FormButtons
          isEditing={false}
          onClickEdit={handleClickEdit}
        />
      </FormPanel>
    );
  };

  if (uiState.isLoading) {
    return <div />;
  }

  return (
    <div data-testid="SecurityPolicyView">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel()}
          {renderForm()}
        </FormLayoutContent>
      </FormLayout>
      <ErrorsDialog data={securityPolicyErrors} wasSecurityPolicyUpdatedWithErrors={uiState.wasSecurityPolicyUpdatedWithErrors} />
    </div>
  );
};

export default withRouter(connector(SecurityPolicyView));
